// Everything below the marker is mirrored verbatim into
// `supabase/functions/_shared/builtin-sections.ts`, which the materializer
// imports. This copy is canonical — regenerate with `bun run sync:shared`.

// ─── shared body ────────────────────────────────────────────────────────────

// The system tier of meeting types: the invariant agenda behind each built-in
// key, above the root team. A team inherits from it the way it inherits from a
// parent, so a workspace that has configured nothing still runs the company's
// Weekly with the company's agenda.

export interface MeetingSectionDef {
  /** Stable id used in UI/state. Built-ins use known section keys; custom keys begin with `custom-`. */
  key: string;
  label: string;
  /** Target duration in minutes. */
  length: number;
  /** True for user-added sections that have no built-in UI. */
  custom: boolean;
}

export const WEEKLY_MEETING_SECTIONS: MeetingSectionDef[] = [
  { key: 'wins', label: 'Wins', length: 5, custom: false },
  { key: 'scorecard', label: 'Scorecard', length: 5, custom: false },
  { key: 'rocks', label: 'Rocks', length: 5, custom: false },
  { key: 'headlines', label: 'Headlines', length: 5, custom: false },
  { key: 'tasks', label: 'To-Dos', length: 5, custom: false },
  { key: 'issues', label: 'Issues', length: 60, custom: false },
  { key: 'conclusion', label: 'Conclusion', length: 5, custom: false },
];

export const QUARTERLY_MEETING_SECTIONS: MeetingSectionDef[] = [
  { key: 'quarterly-checkin', label: 'Check-in', length: 15, custom: false },
  { key: 'quarterly-review', label: 'Review', length: 30, custom: false },
  { key: 'quarterly-vision', label: 'Vision', length: 60, custom: false },
  { key: 'quarterly-team-building', label: 'Team-Building', length: 60, custom: false },
  { key: 'quarterly-rocks', label: 'Rocks', length: 120, custom: false },
  { key: 'quarterly-scorecard', label: 'Scorecard', length: 15, custom: false },
  { key: 'quarterly-people', label: 'People', length: 15, custom: false },
  { key: 'quarterly-issues', label: 'Issues', length: 150, custom: false },
  { key: 'quarterly-conclusion', label: 'Conclusion', length: 15, custom: false },
];

export const ANNUAL_MEETING_SECTIONS: MeetingSectionDef[] = [
  { key: 'annual-d1-checkin', label: 'Check-in', length: 60, custom: false },
  { key: 'annual-review', label: 'Review', length: 60, custom: false },
  { key: 'annual-team-building', label: 'Team-Building', length: 45, custom: false },
  { key: 'annual-checkup', label: 'Check-Up', length: 90, custom: false },
  { key: 'annual-vision', label: 'Vision', length: 120, custom: false },
  { key: 'annual-swot', label: 'SWOT', length: 45, custom: false },
  { key: 'annual-3yr', label: '3-Year Picture', length: 60, custom: false },
  { key: 'annual-d2-checkin', label: 'Day 2: Check-in', length: 15, custom: false },
  { key: 'annual-d2-recap', label: 'Day 2: Recap', length: 15, custom: false },
  { key: 'annual-goals', label: 'Goals', length: 120, custom: false },
  { key: 'rocks', label: 'Rocks', length: 120, custom: false },
  { key: 'annual-orgchart', label: 'Org Chart', length: 30, custom: false },
  { key: 'issues', label: 'Issues', length: 180, custom: false },
  { key: 'annual-next-steps', label: 'Next Steps', length: 15, custom: false },
  { key: 'conclusion', label: 'Conclusion', length: 15, custom: false },
];


/**
 * The built-in keys, in the order they are offered.
 *
 * `label` here is the system invariant, not a display string: `useTerms()`
 * renames these for a workspace and never writes back.
 */
export const BUILTIN_TYPE_DEFS = [
  { key: 'weekly', label: 'Weekly', sortOrder: 0, sections: WEEKLY_MEETING_SECTIONS },
  { key: 'quarterly', label: 'Quarterly', sortOrder: 1, sections: QUARTERLY_MEETING_SECTIONS },
  { key: 'yearly', label: 'Annual', sortOrder: 2, sections: ANNUAL_MEETING_SECTIONS },
];

/** The system tier keyed for the resolver: invariant label and agenda per key. */
export const SYSTEM_MEETING_TYPES: Record<string, { label: string; sections: MeetingSectionDef[] }> =
  Object.fromEntries(BUILTIN_TYPE_DEFS.map((t) => [t.key, { label: t.label, sections: t.sections }]));

export interface PresetAgenda {
  label?: string;
  sections: MeetingSectionDef[];
}

export const PRESET_AGENDAS: Record<string, Partial<Record<string, PresetAgenda>>> = {
  eos: {
    weekly: {
      label: 'Level 10 Meeting',
      sections: [
        { key: 'wins', label: 'Segue', length: 5, custom: false },
        { key: 'scorecard', label: 'Scorecard', length: 5, custom: false },
        { key: 'rocks', label: 'Rock Review', length: 5, custom: false },
        { key: 'headlines', label: 'Customer/Employee Headlines', length: 5, custom: false },
        { key: 'tasks', label: 'To-Do List', length: 5, custom: false },
        { key: 'issues', label: 'IDS', length: 60, custom: false },
        { key: 'conclusion', label: 'Conclude', length: 5, custom: false },
      ],
    },
    quarterly: { label: 'Quarterly Session', sections: QUARTERLY_MEETING_SECTIONS },
    yearly: { label: 'Annual Planning', sections: ANNUAL_MEETING_SECTIONS },
  },
};

const DEFAULT_SECTION_LABELS: Record<string, string> = Object.fromEntries(
  [...WEEKLY_MEETING_SECTIONS, ...QUARTERLY_MEETING_SECTIONS, ...ANNUAL_MEETING_SECTIONS].map((s) => [s.key, s.label]),
);

export function presetSectionLabel(section: Pick<MeetingSectionDef, 'key' | 'label' | 'custom'>): string | null {
  if (section.custom || !section.label) return null;
  const builtIn = DEFAULT_SECTION_LABELS[section.key];
  return builtIn !== undefined && section.label !== builtIn ? section.label : null;
}

export function systemMeetingTypesFor(
  agendaPreset: string | null | undefined,
  presetsEntitled: boolean,
  presets: Record<string, Partial<Record<string, PresetAgenda>>> = PRESET_AGENDAS,
): Record<string, { label: string; sections: MeetingSectionDef[] }> {
  const preset = presetsEntitled && agendaPreset ? presets[agendaPreset] : undefined;
  if (!preset) return SYSTEM_MEETING_TYPES;
  return Object.fromEntries(Object.entries(SYSTEM_MEETING_TYPES).map(([key, system]) => {
    const agenda = preset[key];
    return [key, agenda ? { label: agenda.label ?? system.label, sections: agenda.sections } : system];
  }));
}
